import React, {useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import Button from '@/components/ui/Button';
import {navigate} from '@reach/router';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import axios from 'axios';

import usePaginatedData from '@/utils/usePaginatedData';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import {useStoreActions} from 'store';
import TableComponent from '@/components/table/Table';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {useAlert, ModalAlert} from '@/components/alerts';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {buildURL} from '@/utils/urlBuilder';
import {Tooltip} from 'components/tooltips';
import {extractErrorMessage} from '@/utils/errorHandling';

interface TranscriptionTask {
    id: number;
    createdOn: string;
    eventLang: string | null;
    eventDatetime: string;
    externalTaskId: string;
    status: string | null;
    eventId: number;
}

interface UserData {
    permissions?: string[];
}

interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    count: number;
    pages: number[] | null;
}

const TranscriptionTasksInTranscriptionListView: React.FC = React.memo(() => {
    const {showError, showSuccess} = useAlert();
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const setSearchQueryState = async (
        setSearchQuery: (query: string) => void,
        value: string,
        setPage: (page: number) => void
    ): Promise<void> => {
        setPage(1);
        setSearchQuery(value);
    };

    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [transcriptionToDelete, setTranscriptionToDelete] = useState<{id: number, externalTaskId: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [loadingGetTranscriptOne, setLoadingGetTranscriptOne] = useState<boolean>(false);
    const [loadingGetTranscriptBatch, setLoadingGetTranscriptBatch] = useState<boolean>(false);
    const [loadingRunMonitor, setLoadingRunMonitor] = useState<boolean>(false);
    const [loadingRunMonitorBatch, setLoadingRunMonitorBatch] = useState<boolean>(false);

    const setReloadTranscriptionTasks = useStoreActions((actions) => actions.transcriptionTasksInTranscription.setReload);

    const {
        data: transcriptionTasksInTranscription,
        loading: loadingTranscriptionTasksInTranscription,
        currentPage,
        setCurrentPage,
        count,
        pages,
    }: PaginatedDataResult<TranscriptionTask> = usePaginatedData(
        window.URLS.transcriptionTasksInTranscriptionResource,
        {
            storeResolver: 'transcriptionTasksInTranscription',
            requestParams: {
                params: {
                    query: searchQuery,
                },
            },
        }
    );

    const userCanAccessTranscriptionTasksInTranscriptionResource = hasPermission('fs.transcription_task.list', userData?.permissions, true);
    const userCanGetTranscriptionData = hasPermission('fs.transcription_task.read', userData?.permissions, true);
    const userCanRunMonitor = hasPermission('fs.transcription_task.syncronize.execute', userData?.permissions, true);

    const closeDeleteConfirm = (): void => {
        setTranscriptionToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!transcriptionToDelete) {return;}

        setIsDeleting(true);
        try {
            await axios.delete(buildURL('transcriptionTaskDetails', {transcription_id: transcriptionToDelete.id}));
            showSuccess('Transcription task deleted successfully');
            closeDeleteConfirm();
            setReloadTranscriptionTasks(true);
        } catch (error) {
            console.error('Error deleting transcription:', error);
            const errorMessage = extractErrorMessage(error, 'Error deleting transcription');
            showError(errorMessage);
            setIsDeleting(false);
        }
    };

    if (!userCanAccessTranscriptionTasksInTranscriptionResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_the_transcription_in_transcription.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const active = currentPage || 1;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const detailUrlGenerator = (rowData: TranscriptionTask): string => {
        return buildEventDetailURL(rowData.eventId);
    };

    const handleGetTranscriptOne = async (): Promise<void> => {
        if (!userCanGetTranscriptionData) {return;}

        setLoadingGetTranscriptOne(true);
        try {
            const response = await axios.get(buildURL('transcriptionProcessGetData'));
            showSuccess(response.data.message || 'Transcripts retrieved successfully');
        } catch (error) {
            console.error('Error getting transcripts:', error);
            const errorMessage = extractErrorMessage(error, 'Error getting transcripts. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingGetTranscriptOne(false);
        }
    };

    const handleGetTranscriptBatch = async (): Promise<void> => {
        if (!userCanGetTranscriptionData) {return;}

        setLoadingGetTranscriptBatch(true);
        try {
            const response = await axios.get(buildURL('transcriptionProcessGetDataList'));
            showSuccess(response.data.message || 'Transcripts retrieved successfully');
        } catch (error) {
            console.error('Error getting transcripts in batch:', error);
            const errorMessage = extractErrorMessage(error, 'Error getting transcripts in batch. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingGetTranscriptBatch(false);
        }
    };

    const handleRunMonitor = async (): Promise<void> => {
        if (!userCanRunMonitor) {return;}

        setLoadingRunMonitor(true);
        try {
            const response = await axios.get(buildURL('transcriptionProcessRunMonitor'));
            showSuccess(response.data.message || 'Monitor started successfully');
        } catch (error) {
            console.error('Error running monitor:', error);
            const errorMessage = extractErrorMessage(error, 'Error running monitor. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingRunMonitor(false);
        }
    };

    const handleRunMonitorBatch = async (): Promise<void> => {
        if (!userCanRunMonitor) {return;}

        setLoadingRunMonitorBatch(true);
        try {
            const response = await axios.get(buildURL('transcriptionProcessRunMonitorList'));
            showSuccess(response.data.message || 'Monitor batch started successfully');
        } catch (error) {
            console.error('Error running monitor batch:', error);
            const errorMessage = extractErrorMessage(error, 'Error running monitor batch. Please try again.');
            showError(errorMessage);
        } finally {
            setLoadingRunMonitorBatch(false);
        }
    };

    const columns = [
        {
            accessorKey: 'createdOn',
            header: i18n.t('th_task_created.text'),
            cell: (cell: any) => {
                return configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false}
                ) : '';
            },
        },
        {
            accessorKey: 'eventLang',
            header: i18n.t('th_language.text'),
            cell: (cell: any) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            accessorKey: 'eventDatetime',
            header: i18n.t('th_call_datetime.text'),
        },
        {
            accessorKey: 'externalTaskId',
            header: i18n.t('th_external_task.text'),
            cell: (cell: any) => (
                <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />
            ),
        },
        {
            accessorKey: 'status',
            header: i18n.t('th_status.text'),
            cell: (cell: any) => {
                if (cell.getValue() === null) {
                    return '-';
                } else {
                    return cell.getValue();
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <>
                    <div className="d-inline-flex">
                        <Tooltip content={i18n.t('jt_view.text')} placement="top">
                            <Button
                                variant="outline-info"
                                size="sm"
                                icon="mdi-eye"
                                onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                            />
                        </Tooltip>

                        <Button
                            variant="outline-danger"
                            size="sm"
                            style={{
                                fontSize: '12px',
                                padding: '0.5rem 1rem',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onClick={() => {
                                setTranscriptionToDelete({id: cell.row.original.id, externalTaskId: cell.row.original.externalTaskId});
                                setDeleteConfirmOpen(true);
                            }}
                        >
                            {i18n.t('jt_delete_transcription.text')}
                        </Button>
                    </div>
                </>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_the_transcription_in_transcription.text')} />

            <Row>
                <Col lg={4}>
                    <p>
                        <b>{i18n.t('jt_calls_submitted.text')} </b>
                        {count}
                    </p>
                </Col>
            </Row>

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event) => inputChange(event)}
                            data-cy="eval-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-center flex-wrap mb-2">
                        {userCanGetTranscriptionData && (
                            <Button
                                variant="info"
                                size="sm"
                                className='mr-2'
                                onClick={handleGetTranscriptOne}
                                disabled={loadingGetTranscriptOne}
                            >
                                {loadingGetTranscriptOne ? 'Getting...' : i18n.t('jt_get_transcripts_by_one.text')}
                            </Button>
                        )}
                        {userCanGetTranscriptionData && (
                            <Button
                                variant="info"
                                size="sm"
                                className='mr-2'
                                onClick={handleGetTranscriptBatch}
                                disabled={loadingGetTranscriptBatch}
                            >
                                {loadingGetTranscriptBatch ? 'Getting...' : i18n.t('jt_get_transcripts_in_batch.text')}
                            </Button>
                        )}
                        {userCanRunMonitor && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className='mr-2'
                                onClick={handleRunMonitor}
                                disabled={loadingRunMonitor}
                            >
                                {loadingRunMonitor ? 'Starting...' : i18n.t('jt_run_monitor.text')}
                            </Button>
                        )}
                        {userCanRunMonitor && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRunMonitorBatch}
                                disabled={loadingRunMonitorBatch}
                            >
                                {loadingRunMonitorBatch ? 'Starting...' : i18n.t('jt_run_monitor_in_batch.text')}
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            {loadingTranscriptionTasksInTranscription ? (
                <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
            ) : (
                <>
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={transcriptionTasksInTranscription} columns={columns} />
                        </div>
                    </div>
                    <PaginationComponent
                        pages={pages}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </>
            )}
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={i18n.t('es_are_you_sure_delete_task.text', {externalTaskId: transcriptionToDelete?.externalTaskId})}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

export default TranscriptionTasksInTranscriptionListView; 